/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import {
  getCostDatabaseItems,
  getEstimateItems,
  getMeasurementUnits,
  takeoffWizardResultsErrors,
} from "@Actions/estimation";
import SlickGrid1, { type SlickGrid1Ref, type GridRowBase } from "@CubeComponents/html/SlickGrid1";
import IconComponent from "@CubeComponents/icons";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import _ from "lodash";
import Shimmer from "@Components/Queries/Shimmer";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR } from "@Services/constants";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { getModelPropertiesData } from "@Services/utils/modelPropertiesDataDB";
import { modelPropertiesResponse } from "@Actions/Viewer";
import { useModelPropertiesWiped } from "@Services/useModelPropertiesWiped";
import ModelDataClearedState from "@Features/design-viewer/components/Modals/ModelDataClearedState";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import {
  extractPropertyPartialName,
  getModifiedNodesList,
} from "@Features/viewer-modals/components/Modal/SearchSet/searchSetHelper";
import Text from "@CubeComponents/html/Text/Text";
import type { RootState } from "src/reducers";
import type {
  EstimateData as EstimateDataState,
  GetEstimateItemsResponse,
} from "@ServerTypes/estimates/estimates.schema";
import type { GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
import type { GetAllCostDatabaseItemsResponse } from "@ServerTypes/costDatabase/costDatabase.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type WizardResultsProps = {
  estimateId: ObjectIdString;
  fileVersionId?: ObjectIdString;
  wizardDetails: {
    quantityParameter: string;
    codeField: string;
    unitField: string;
  };
  setErrorInAnyRecord: (value: boolean) => void;
  isResultLoadingDone: boolean;
  setIsResultLoadingDone: (value: boolean) => void;
  setIsAnyRecordAvailableToAdd?: (value: boolean) => void;
  targetFileVersionId?: string[];
};

type WizardCalculatedFields = {
  remarks?: string;
  quantity?: number | string;
  code?: string;
  unit?: string;
  calculatedValueByQP?: number | null;
  calculatedValueForCode?: string | null;
  errorSet?: Set<string>;
};

// ADDED: WizardNode mirrors the dot-notation flattened model node shape produced by
// `getModifiedNodesList`. The shape is dynamic (per-file properties).
type WizardNode = GridRowBase & {
  nodeId?: string | number;
  properties?: { [k: string]: unknown };
  typeProperties?: { [k: string]: unknown };
  customProperties?: { [k: string]: unknown };
  wizardCalulatedFields?: WizardCalculatedFields;
};

// Derived from server schema: a single measurement unit item from getMeasurementUnitsResponse.result
type MeasurementUnitRecord = NonNullable<GetMeasurementUnitsResponse["result"]>[number];

// Derived from server schema: a single Cost Database/estimate item entry returned from
// getAllCostDatabaseItemsResponse / getEstimateItemsResponse (both: z.record(z.string(), z.unknown())).
// We use the same shape but extend with the specific fields the component reads.
type CostDatabaseItemRecord = NonNullable<GetAllCostDatabaseItemsResponse["result"]>[number] & {
  code: string;
  unitName?: string;
  parentsList?: Array<{ itemId?: string }>;
  itemId?: string;
  measurementUnit?: { name?: string };
};

type EstimateItemRecord = NonNullable<GetEstimateItemsResponse["result"]>[number] & {
  code?: string;
  measurementUnit?: { name?: string };
};

// ADDED: SlickGrid column definition shape used in this file. Renders with `formatter` and
// `_excelValueGetter` per SlickGrid1 conventions (SlickGrid Formatter typing exception).
type WizardGridColumn = {
  field: string;
  id: string;
  name: string;
  textAlign?: "Left" | "Right" | "Center";
  minWidth?: number;
  formatter?: (data: unknown, value: unknown, a: unknown, b: unknown, params: WizardNode) => HTMLElement;
  _excelValueGetter?: (row: WizardNode) => unknown;
  filterType?: string;
};

const WizardResults = ({
  estimateId,
  fileVersionId,
  wizardDetails,
  setErrorInAnyRecord,
  isResultLoadingDone,
  setIsResultLoadingDone,
  setIsAnyRecordAvailableToAdd,
  targetFileVersionId,
}: WizardResultsProps) => {
  const { t } = useTranslation();
  const [unitAndItemsLoadDone, setUnitAndItemsLoadDone] = useState(false);
  const [gridColumns, setGridColumns] = useState<WizardGridColumn[]>([]);
  const [results, setResults] = useState<WizardNode[] | null>(null);
  const [measurementUnitsMap, setMeasurementUnitsMap] = useState<{ [k: string]: MeasurementUnitRecord }>({});
  const [costDatabaseItemsMap, setCostDatabaseItemsMap] = useState<{ [k: string]: CostDatabaseItemRecord }>({});
  const [estimateAttachedCostDatabaseItems, setEstimateAttachedCostDatabaseItems] = useState<EstimateItemRecord[]>([]);
  // Only relevant for the fileVersionId case (current file read from IDB); a
  // targetFileVersionId is fetched fresh, so it can't be "wiped".
  const isModelDataWiped = useModelPropertiesWiped(targetFileVersionId?.length ? undefined : fileVersionId);

  const { estimateData } = useSelector((state: RootState) => ({
    estimateData: state.estimateData as EstimateDataState & {
      costDatabase?: { id?: ObjectIdString; costDatabaseVersionId?: ObjectIdString };
    },
  }));
  const modelProperties = useSelector((state: RootState) => state.modelProperties);
  const resultsGrid = useRef<SlickGrid1Ref<WizardNode> | null>(null);

  const dispatch = useDispatch();

  const remarksFormatter = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: WizardNode) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    container.style.width = "100%";
    container.style.overflow = "hidden";
    if (params.wizardCalulatedFields?.remarks) {
      root.render(
        <Text tooltipContent={params.wizardCalulatedFields?.remarks as TranslatedString}>
          <div className="flex gap-2 text-negative_communication">
            <IconComponent color="#e75555" showGradient name="details" width="1.25rem" height="1.25rem" />
            {params.wizardCalulatedFields?.remarks}
          </div>
        </Text>,
      );
    }

    return container;
  };

  const getColumns = (): WizardGridColumn[] => {
    const columns: WizardGridColumn[] = [
      {
        field: "nodeId",
        id: "nodeId",
        name: t("CUBE ID"),
        textAlign: "Left",
        minWidth: 100,
      },
      {
        id: "propertiesCUBE Name",
        field: "properties.CUBE Name",
        name: extractPropertyPartialName("properties.CUBE Name"),
        minWidth: 150,
        textAlign: "Left",
      },
    ];

    return [
      ...columns,
      ...[wizardDetails.quantityParameter, wizardDetails.codeField, wizardDetails.unitField].map((column: string) => {
        const columnName = extractPropertyPartialName(column);
        return {
          field: column,
          id: column.replace(/\./g, ""),
          name: columnName,
          textAlign: "Left" as const,
          minWidth: 150,
        };
      }),
      {
        field: "wizardCalulatedFields.remarks",
        id: "remarks",
        name: t("Remarks"),
        textAlign: "Left",
        minWidth: 300,
        formatter: remarksFormatter,
        _excelValueGetter: (row: WizardNode) => row.wizardCalulatedFields?.remarks ?? "",
        filterType: "Text",
      },
    ];
  };

  const unitSetter = (units: MeasurementUnitRecord[]) => {
    const newObj: { [k: string]: MeasurementUnitRecord } = {};
    units.map((unit: MeasurementUnitRecord) => {
      if (unit.name) {
        newObj[unit.name.trim().toLowerCase()] = unit;
      }
    });
    setMeasurementUnitsMap(newObj);
  };

  const itemsSetter = (
    costDatabaseItems: CostDatabaseItemRecord[],
    estimateCostDatabaseItems: EstimateItemRecord[],
  ) => {
    const mapper: { [k: string]: CostDatabaseItemRecord } = {};
    const estimateItemsMapper: { [k: string]: EstimateItemRecord } = {};

    estimateCostDatabaseItems.map((item: EstimateItemRecord) => {
      if (item.code) {
        estimateItemsMapper[item.code] = item;
      }
    });

    costDatabaseItems.map((item: CostDatabaseItemRecord) => {
      const itemData: CostDatabaseItemRecord = {
        ...item,
        code: item.code,
        unitName: _.get(item, "measurementUnit.name", "").trim().toLowerCase(),
        parentsList: item.parentsList,
        itemId: item.itemId,
      };

      const matchedEstimateItem = estimateItemsMapper[itemData.code];
      if (matchedEstimateItem) {
        itemData.unitName = (_.get(matchedEstimateItem, "measurementUnit.name", "") as string).trim().toLowerCase();
      }

      mapper[itemData.code.trim()] = itemData;
    });

    setCostDatabaseItemsMap(mapper);
  };

  const init = async () => {
    const costDatabaseId = estimateData.costDatabase?.id;
    const costDatabaseVersionId = estimateData.costDatabase?.costDatabaseVersionId;
    if (!costDatabaseId || !costDatabaseVersionId) {
      dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
      return;
    }

    const [unitRes, costDatabaseItemsRes, estimateCostDatabaseItemsRes] = await Promise.all([
      getMeasurementUnits({}),
      getCostDatabaseItems({
        value: {
          params: {
            costDatabaseId: costDatabaseId,
            versionId: costDatabaseVersionId,
            getAllItems: true,
          },
        },
      }),
      getEstimateItems({
        value: {
          params: {
            estimateId,
            getAllItems: true,
            getCostDatabaseItemsOnly: true,
          },
        },
      }),
    ]);

    if (!unitRes.data.status || !costDatabaseItemsRes.data.status || !estimateCostDatabaseItemsRes.data.status) {
      dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
      return;
    }

    unitSetter((unitRes.data.result ?? []) as MeasurementUnitRecord[]);
    itemsSetter(
      (costDatabaseItemsRes.data.result ?? []) as CostDatabaseItemRecord[],
      (estimateCostDatabaseItemsRes.data.result ?? []) as EstimateItemRecord[],
    );
    setEstimateAttachedCostDatabaseItems((estimateCostDatabaseItemsRes.data.result ?? []) as EstimateItemRecord[]);
    setUnitAndItemsLoadDone(true);
  };

  useEffect(() => {
    init();
  }, []);

  const getAllQPValueFields = (QP: string, nodeData: WizardNode) => {
    const fields: string[] = [];
    for (let key of Object.keys(nodeData)) {
      if (typeof key === "string") {
        const value = key.split("/").at(-1);
        if (value === QP) {
          fields.push(key);
        }
      }
    }
    return fields;
  };

  const setParentsMapper = (items: Array<CostDatabaseItemRecord | EstimateItemRecord>, uniqueParentId: Set<string>) => {
    for (const item of items) {
      if (!_.get(item, "parentsList.length")) continue;

      for (const record of (item as CostDatabaseItemRecord).parentsList ?? []) {
        if (record.itemId) {
          uniqueParentId.add(record.itemId);
        }
      }
    }
  };

  const getAllItemParentsList = (nodeGeneratedItems: CostDatabaseItemRecord[], estimateItems: EstimateItemRecord[]) => {
    const parentsSet = new Set<string>();
    setParentsMapper(nodeGeneratedItems, parentsSet);
    setParentsMapper(estimateItems, parentsSet);
    return parentsSet;
  };

  const getGridItems = (data: WizardNode[]) => {
    let isError = false;
    let isAnyCorrectRecord = false;
    const itemToFilterByHierarchy: CostDatabaseItemRecord[] = [];
    const uniqueCodeMap: { [k: string]: number } = {};

    for (let nodeData of data) {
      const errorSet = new Set<string>();
      const QP = _.get(nodeData, wizardDetails.quantityParameter);
      const unit = _.get(nodeData, wizardDetails.unitField);
      const itemCode = _.get(nodeData, wizardDetails.codeField);
      let QPValue: number | null = null;

      if (
        typeof QP !== "string" ||
        !QP.trim() ||
        typeof unit !== "string" ||
        !unit.trim() ||
        typeof itemCode !== "string" ||
        !itemCode.trim()
      ) {
        errorSet.add(takeoffWizardResultsErrors.VALUE_MISSING);
      }

      if (typeof QP === "string" && QP.trim()) {
        if (QP.includes("/")) {
          const splitData = QP.split("/");
          let path;
          if (splitData[0] === "Instance") {
            path = "properties." + splitData.slice(1).join("/");
          } else if (splitData[0] === "Type") {
            path = "typeProperties." + splitData.slice(1).join("/");
          }
          if (path) {
            QPValue = Number(_.get(nodeData, path));
          }
        } else {
          const fieldsRelatedToQp = getAllQPValueFields(QP, nodeData);
          if (fieldsRelatedToQp.length === 0) {
            errorSet.add(takeoffWizardResultsErrors.INVALID_PARAMETER);
          } else if (fieldsRelatedToQp.length > 1) {
            errorSet.add(takeoffWizardResultsErrors.DUPLICATE_PARAMETER);
          } else {
            const fieldPath = fieldsRelatedToQp[0];
            if (!fieldPath) {
              errorSet.add(takeoffWizardResultsErrors.INVALID_PARAMETER);
            } else {
              QPValue = Number(_.get(nodeData, fieldPath));
            }
          }
        }

        if (Number.isNaN(QPValue)) {
          errorSet.add(takeoffWizardResultsErrors.INVALID_PARAMETER);
        }
      }

      if (typeof itemCode === "string" && itemCode.trim()) {
        const mappedItem = costDatabaseItemsMap[itemCode.trim()];
        if (!mappedItem) {
          errorSet.add(takeoffWizardResultsErrors.ITEM_NOT_FOUND);
        } else if (typeof unit === "string" && unit.trim()) {
          const costDatabaseUnitName = mappedItem.unitName;
          const costDatabaseUnitType = costDatabaseUnitName
            ? measurementUnitsMap[costDatabaseUnitName]?.type?.name
            : undefined;
          const inputUnitType = measurementUnitsMap[unit.trim().toLowerCase()]?.type?.name;
          if (!costDatabaseUnitName || costDatabaseUnitType !== inputUnitType) {
            errorSet.add(takeoffWizardResultsErrors.UNIT_MISMATCH);
          }
        }
      }

      const calculatedValueForCode = typeof itemCode === "string" ? itemCode : null;
      nodeData.wizardCalulatedFields = {
        calculatedValueByQP: QPValue,
        calculatedValueForCode,
        errorSet,
      };

      if (typeof itemCode === "string" && itemCode.trim() && !Object.hasOwn(uniqueCodeMap, itemCode.trim())) {
        const mappedItem = costDatabaseItemsMap[itemCode.trim()];
        if (mappedItem) {
          itemToFilterByHierarchy.push(mappedItem);
          uniqueCodeMap[itemCode.trim()] = 1;
        }
      }
    }

    const uniqueParentsId = getAllItemParentsList(itemToFilterByHierarchy, estimateAttachedCostDatabaseItems);

    data.map((item: WizardNode) => {
      const code = item.wizardCalulatedFields?.calculatedValueForCode;
      if (typeof code === "string" && code.trim() && Object.hasOwn(costDatabaseItemsMap, code.trim())) {
        const costDatabaseItem = costDatabaseItemsMap[code.trim()];
        if (costDatabaseItem?.itemId && uniqueParentsId.has(costDatabaseItem.itemId)) {
          item.wizardCalulatedFields?.errorSet?.add(takeoffWizardResultsErrors.INVALID_PARAMETER);
        }
      }

      let errorDetails = "";
      let index = 0;
      const errorSet = item.wizardCalulatedFields?.errorSet ?? new Set<string>();
      const size = errorSet.size;

      for (const error of errorSet) {
        errorDetails += error;
        index++;
        if (index < size) {
          errorDetails += ", ";
        }
      }

      if (errorDetails && item.wizardCalulatedFields) {
        item.wizardCalulatedFields.remarks = errorDetails;
        isError = true;
      } else {
        isAnyCorrectRecord = true;
      }
    });

    setResults(data);
    if (isError) setErrorInAnyRecord(true);
    if (setIsAnyRecordAvailableToAdd && isAnyCorrectRecord) {
      setIsAnyRecordAvailableToAdd(true);
    }
  };

  useEffect(() => {
    if (modelProperties.isLoaded && unitAndItemsLoadDone) {
      const loadAndSetResults = async () => {
        // Fetch model properties on demand here: a target version is fetched (it
        // may not be in IDB); otherwise the current file is read straight from
        // IDB. Fed directly into getModifiedNodesList — no stored per-instance copy.
        let data: { [k: string]: { [k: string]: unknown } };
        if (targetFileVersionId) {
          data = (await modelPropertiesResponse(targetFileVersionId)) ?? {};
        } else {
          // Exactly one of targetFileVersionId / fileVersionId is provided; in this
          // branch it's fileVersionId (the current file). getModelPropertiesData
          // resolves the constituent ids + units internally from originalFileVersionId.
          data = await getModelPropertiesData({ originalFileVersionId: fileVersionId ?? "" });
        }
        const modifiedData = getModifiedNodesList(data) as WizardNode[];
        setGridColumns(getColumns());
        getGridItems(modifiedData);
        setIsResultLoadingDone(true);
      };
      loadAndSetResults();
    }
  }, [modelProperties.isLoaded, unitAndItemsLoadDone]);

  if (isModelDataWiped) return <ModelDataClearedState />;

  const getResultsGrid = () => (
    <div className="overflow-auto h-100">
      <SlickGrid1
        ref={resultsGrid}
        showPageCount={false}
        columnDefinitions={gridColumns as unknown as Parameters<typeof SlickGrid1>[0]["columnDefinitions"]}
        rowHeight={40 as unknown as Parameters<typeof SlickGrid1>[0]["rowHeight"]}
        enableGridMenu
        dataset={results as unknown as Parameters<typeof SlickGrid1>[0]["dataset"]}
        excelExport
      />
    </div>
  );

  return isResultLoadingDone ? (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2 text-base font-semibold">
        <IconComponent name="results" width="1.5rem" height="1.5rem" />
        <Text>{t("Results")}</Text>
      </div>
      {getResultsGrid()}
    </div>
  ) : (
    <Shimmer />
  );
};

export default WizardResults;
